import React, { useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { blogApi } from '../services/api';
import { BlogPost } from '../types';

const BlogManagement: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<BlogPost>({
    title: '',
    content: '',
    shortDescription: '',
    isPublished: true,
    tags: '',
    lang: 'uz',
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const data = await blogApi.getAllPosts();
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await blogApi.updatePost(editingPost.id!, formData);
      } else {
        await blogApi.createPost(formData);
      }
      await loadPosts();
      resetForm();
      alert('Post saved successfully!');
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post');
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({ ...post, lang: post.lang || 'uz' });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogApi.deletePost(id);
        await loadPosts();
        alert('Post deleted successfully!');
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error deleting post');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      shortDescription: '',
      isPublished: true,
      tags: '',
      lang: 'uz',
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: '#666', fontSize: '1.1rem' }}>Loading posts...</div>;
  }

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem'
  };

  const btnStyle = {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center' as const,
    transition: 'all 0.3s ease'
  };

  const primaryBtn = { ...btnStyle, backgroundColor: '#007bff', color: 'white' };
  const secondaryBtn = { ...btnStyle, backgroundColor: '#6c757d', color: 'white' };
  const dangerBtn = { ...btnStyle, backgroundColor: '#dc3545', color: 'white' };
  const smallBtn = { ...btnStyle, padding: '0.5rem 1rem', fontSize: '0.8rem' };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={{ color: '#333', margin: 0 }}>Blog Management</h1>
        <button 
          style={primaryBtn}
          onClick={() => setShowForm(true)}
        >
          New Post
        </button>
      </div>

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '95vh',
            overflow: 'auto',
            margin: '1rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #eee'
            }}>
              <h2 style={{ margin: 0, color: '#333' }}>
                {editingPost ? 'Edit Post' : 'New Post'}
              </h2>
              <button 
                onClick={resetForm}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  width: '2rem',
                  height: '2rem',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#555', fontWeight: '500' }}>
                  Title:
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#555', fontWeight: '500' }}>
                  Short Description:
                </label>
                <textarea
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  rows={3}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#555', fontWeight: '500' }}>
                  Content (Markdown):
                </label>
                <div data-color-mode="light">
                  <MDEditor
                    value={formData.content}
                    onChange={(val) => setFormData(prev => ({ ...prev, content: val || '' }))}
                    height={500}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#555', fontWeight: '500' }}>
                  Tags (comma-separated):
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags || ''}
                  onChange={handleChange}
                  placeholder="react, typescript, tutorial"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#555', fontWeight: '500' }}>
                  Language:
                </label>
                <select
                  name="lang"
                  value={formData.lang || 'uz'}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="uz">O'zbekcha</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Published
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" style={secondaryBtn} onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" style={primaryBtn}>
                  {editingPost ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {posts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#666',
            background: 'white',
            borderRadius: '8px',
            border: '1px solid #eee'
          }}>
            <p>No blog posts yet. Create your first post!</p>
          </div>
        ) : (
          posts.map(post => (
            <div key={post.id} style={{
              background: 'white',
              border: '1px solid #eee',
              borderRadius: '8px',
              padding: '1.5rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <h3 style={{ margin: 0, color: '#333', flex: 1, marginRight: '1rem' }}>
                  {post.title}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    style={{ ...smallBtn, ...secondaryBtn }}
                    onClick={() => handleEdit(post)}
                  >
                    Edit
                  </button>
                  <button 
                    style={{ ...smallBtn, ...dangerBtn }}
                    onClick={() => handleDelete(post.id!)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <p style={{ color: '#666', lineHeight: 1.6, marginBottom: '1rem' }}>
                {post.shortDescription}
              </p>
              
              <div style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                fontSize: '0.9rem',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.8rem',
                  backgroundColor: post.isPublished ? '#d4edda' : '#fff3cd',
                  color: post.isPublished ? '#155724' : '#856404'
                }}>
                  {post.isPublished ? 'Published' : 'Draft'}
                </span>

                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontWeight: '500',
                  fontSize: '0.8rem',
                  backgroundColor: '#e2e3ff',
                  color: '#3f3fa3'
                }}>
                  {post.lang === 'en' ? 'English' : post.lang === 'uz' ? "O'zbekcha" : 'No language'}
                </span>

                {post.tags && (
                  <span style={{ color: '#666' }}>
                    Tags: {post.tags}
                  </span>
                )}
                
                {post.createdAt && (
                  <span style={{ color: '#666' }}>
                    {formatDate(post.createdAt)}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogManagement;